import React from "react";
import FormCheckbox from "src/shared/components/FormCheckbox";
import CategorySelect from "./CategorySelect";
import ImageUploader from "../../ImageUploader";
import { Category, StaffMember, ProductFormData } from "src/shared/services/interfacev2";
import { formatAmount, removeEmojis } from "../utils/formatters";
import StaffSelect from "./StaffSelect";
import InputGroup from "src/business-types/grocery/layouts/setting/customer/InputGroup";
import CustomDropdownSelect from "src/business-types/grocery/components/DropdownSelect";
import { useUser } from "src/shared/context/UserContext";
import { normalizeFoodTypes } from "src/shared/utils/normalizeFoodTypes";
import BarcodeSection from "src/business-types/grocery/layouts/setting/Products/shared/BarcodeSection";

interface ProductFormProps {
  formData: ProductFormData;
  categories: Category[];
  staffMembers: StaffMember[];
  businessType: string;
  onChange: (field: keyof ProductFormData, value: any) => void;
  showErrors?: boolean;
  previewImage?: string | null;
  showReorderPosition?: boolean;
  barcodeId?: string;
  barcodeMode?: boolean;
  scanMode?: boolean;
  onToggleScan?: () => void;
  onBarcodeDetected?: (barcode: string) => void;
}

const ProductForm: React.FC<ProductFormProps> = ({
  formData,
  categories,
  staffMembers,
  businessType,
  onChange,
  showErrors = false,
  previewImage,
  showReorderPosition = false,
  barcodeId = "",
  barcodeMode = false,
  scanMode = false,
  onToggleScan = () => {},
  onBarcodeDetected = () => {},
}) => {
  const { userConfigration } = useUser();
  const handleProductNameChange = (value: string) => {
    onChange("productName", removeEmojis(value));
  };

  const foodTypes = userConfigration?.foodTypes ? normalizeFoodTypes(userConfigration.foodTypes) : ["Veg", "Non-Veg"];

  return (
    <div className="space-y-3">
      {/* Top Section */}
      <div className="grid grid-cols-[1fr_140px] gap-4 items-start">
        <div className="space-y-2">
          <InputGroup label="Product name" name="productName" type="text" mandatory value={formData.productName} onChange={(_, value) => handleProductNameChange(value)} />
          <InputGroup label="Arabic name" name="arabicName" type="text" mandatory value={formData.arabicName} onChange={(_, value) => onChange("arabicName", removeEmojis(value))} />
        </div>
        <div className="flex justify-end">
          <ImageUploader value={formData.image ?? null} previewImage={previewImage} onChange={(file) => onChange("image", file)} maxSizeMB={5} />
        </div>
      </div>

      {/* Product price and Reorder position - Side by side when reorder is shown, otherwise full width */}
      {showReorderPosition ? (
        <div className="grid grid-cols-2 gap-4">
          <InputGroup
            label="Product price"
            name="amount"
            type="number"
            mandatory
            value={formData.amount}
            onChange={(_, value) => {
              const formatted = formatAmount(value);
              onChange("amount", formatted);
            }}
            error={formData.amount === "0" || parseFloat(formData.amount) < 0}
            helperText={formData.amount === "0" ? "Price must be greater than 0" : parseFloat(formData.amount) < 0 ? "Price cannot be negative" : ""}
          />
          <div className="pt-1">
            <InputGroup label="Reorder position" name="displayOrder" type="number" value={formData.displayOrder ?? ""} onChange={(_, value) => onChange("displayOrder", value)} />
          </div>
        </div>
      ) : (
        <InputGroup
          label="Product price"
          name="amount"
          type="number"
          mandatory
          value={formData.amount}
          onChange={(_, value) => {
            const formatted = formatAmount(value);
            onChange("amount", formatted);
          }}
          error={formData.amount === "0" || parseFloat(formData.amount) < 0}
          helperText={formData.amount === "0" ? "Price must be greater than 0" : parseFloat(formData.amount) < 0 ? "Price cannot be negative" : ""}
        />
      )}

      {/* Type and Category - Side by side for Restaurant only */}
      {businessType === "Restaurant" ? (
        <div className="grid grid-cols-2 gap-4">
          <CustomDropdownSelect
            name="type"
            title="Type"
            value={formData.type ?? ""}
            mandatory
            options={foodTypes.map((type: string) => ({
              _id: type,
              CategoryName: type,
            }))}
            titleofSelect="Select type"
            onChange={(value) => onChange("type", value)}
          />
          <CategorySelect categories={categories} value={formData.categoryId} onChange={(v) => onChange("categoryId", v)} required showError={showErrors} />
        </div>
      ) : (
        <CategorySelect categories={categories} value={formData.categoryId} onChange={(v) => onChange("categoryId", v)} required showError={showErrors} />
      )}

      {businessType === "Saloon" && <StaffSelect staffMembers={staffMembers} value={formData.staffList} onChange={(v) => onChange("staffList", v)} />}

      {/* BarcodeSection - always rendered */}
      {businessType === "Restaurant" && (
        <div className="border border-gray-300 rounded-lg p-3">
          <BarcodeSection barcodeId={barcodeId || formData.barcodeId || ""} barcodeMode={barcodeMode} scanMode={scanMode} onToggleScan={onToggleScan} onDetected={onBarcodeDetected} />
        </div>
      )}

      <FormCheckbox label="Its Inward Product" checked={formData.ownProduct} onChange={(_, checked) => onChange("ownProduct", checked)} />
    </div>
  );
};

export default ProductForm;
